import { useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlarmClock,
  BellOff,
  Coins,
  CreditCard,
  Gift,
  RotateCcw,
  ShieldCheck,
  Trash2,
  UserX,
} from 'lucide-react';
import { AudienceFilters } from '../components/AudienceFilters';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Drawer } from '../components/Drawer';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  DataTable,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  Spinner,
  Tabs,
  type Column,
} from '../components/ui';
import { api, compact } from '../lib/api';
import { useDebounced } from '../lib/debounce';
import { useT, type Translate } from '../lib/i18n';
import {
  RecordNav,
  useOnwardTrail,
  useOpenRecord,
  useTrail,
  deviceAlarmsPath,
  type Trail,
} from '../lib/navigation';
import {
  formatCredits,
  formatDateTime,
  formatNumber,
  formatPlatform,
  formatPercent,
  formatRelative,
  formatSchedule,
  humanize,
} from '../lib/format';
import { errorMessage, useToast } from '../lib/toast';
import type {
  AdminAlarm,
  AdminFreeTrialEvents,
  AdminUserDetail,
  AdminUserSummary,
  AudienceFilter,
  BalanceEvent,
  Paginated,
  PayoutAllocation,
  WalletAdjustmentResult,
  WalletOperation,
} from '../lib/types';

const SORTS = [
  { value: 'registered', label: 'users.sort.registered' },
  { value: 'last_seen', label: 'users.sort.lastSeen' },
  { value: 'balance', label: 'users.sort.balance' },
  { value: 'earned', label: 'users.sort.earned' },
  { value: 'alarms', label: 'users.sort.alarms' },
  { value: 'wake_rate', label: 'users.sort.wakeRate' },
] as const;

/** Long enough to cover a word being typed, short enough to feel live. */
const FILTER_DEBOUNCE_MS = 300;

export function UsersPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const open = useOpenRecord();
  const [filter, setFilter] = useState<AudienceFilter>({});
  const [sort, setSort] = useState<string>('registered');
  const [page, setPage] = useState(1);

  // Which account is open is part of the address, so a user reached from an
  // alarm — or from a colleague's link — survives a reload.
  const selected = params.get('user');

  // The name and device searches are typed a letter at a time; one request
  // per pause rather than per keystroke.
  const debouncedFilter = useDebounced(filter, FILTER_DEBOUNCE_MS);

  const query = useMemo(
    () => compact({ ...debouncedFilter, sort, page, limit: 25 }),
    [debouncedFilter, sort, page],
  );

  const users = useQuery({
    queryKey: ['users', query],
    queryFn: () => api.get<Paginated<AdminUserSummary>>('/api/users', query),
    // The previous page stays on screen while the next search loads, rather
    // than the table collapsing to a skeleton on every keystroke.
    placeholderData: keepPreviousData,
  });

  const columns: Column<AdminUserSummary>[] = [
    {
      key: 'device',
      header: t('users.column.account'),
      render: (user) => {
        const name = fullName(user);
        return (
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0">
              {name && (
                <p className="truncate text-sm font-medium text-ink">{name}</p>
              )}
              <p
                className={
                  name
                    ? 'font-mono text-[11px] break-anywhere text-ink-3'
                    : 'font-mono text-xs break-anywhere'
                }
              >
                {user.deviceId}
              </p>
            </div>
            {!user.isActive && <Badge tone="danger">{t('common.off')}</Badge>}
          </div>
        );
      },
    },
    {
      key: 'platform',
      header: t('users.column.platform'),
      desktopOnly: true,
      render: (user) => (
        <span className="text-ink-2">
          {formatPlatform(user.platform)}
          {user.country && ` · ${user.country}`}
          {user.currentAppVersion && (
            <span className="block font-mono text-[11px] text-ink-3">
              v{user.currentAppVersion}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'balance',
      header: t('users.column.balance'),
      numeric: true,
      render: (user) => (
        <span title={t('users.stakedTitle', { amount: user.blockedBalance })}>
          {formatNumber(user.balance)}
          {user.blockedBalance > 0 && (
            <span className="ml-1 text-xs text-ink-3">
              {' '}
              {t('users.stakedInline', {
                amount: formatNumber(user.blockedBalance),
              })}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'earned',
      header: t('users.column.won'),
      numeric: true,
      desktopOnly: true,
      render: (user) => formatNumber(user.earnedBalance),
    },
    {
      key: 'alarms',
      header: t('users.column.alarms'),
      numeric: true,
      render: (user) => `${user.enabledAlarmCount}/${user.alarmCount}`,
    },
    {
      key: 'wake',
      header: t('users.column.wakeRate'),
      numeric: true,
      render: (user) => <WakeRate user={user} />,
    },
    {
      key: 'seen',
      header: t('users.column.lastSeen'),
      numeric: true,
      render: (user) => (
        <span className="text-ink-2">{formatRelative(user.lastSeenAt)}</span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('users.title')}
        description={t('users.description')}
        action={
          <Field label={t('users.sortBy')} className="w-44">
            <Select
              value={sort}
              onChange={(event) => {
                setSort(event.target.value);
                setPage(1);
              }}
            >
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </Select>
          </Field>
        }
      />

      <div className="space-y-4">
        <AudienceFilters
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />

        {users.isError && <ErrorNote error={users.error} />}

        <Card
          className={
            users.isPlaceholderData
              ? 'opacity-60 transition-opacity'
              : undefined
          }
        >
          {users.isPending ? (
            <Skeleton className="h-96" />
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={users.data?.items ?? []}
                rowKey={(user) => user.id}
                onRowClick={(user) => open({ kind: 'user', id: user.id })}
              />
              <Pagination
                page={users.data?.page ?? 1}
                totalPages={users.data?.totalPages ?? 1}
                total={users.data?.total ?? 0}
                onChange={setPage}
              />
            </>
          )}
        </Card>
      </div>

      {selected && (
        <UserDrawer
          userId={selected}
          onClose={() =>
            setParams(
              (next) => {
                next.delete('user');
                next.delete('from');
                return next;
              },
              { replace: true },
            )
          }
        />
      )}
    </>
  );
}

/** "First Last", or whichever half is set; null on an unclaimed account. */
function fullName(user: AdminUserSummary): string | null {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || null;
}

/**
 * A rate with nothing behind it is not zero, it is unknown — and rendering the
 * two the same way is how a brand-new account ends up in a "struggling users"
 * list.
 */
function WakeRate({ user }: { user: AdminUserSummary }) {
  if (user.wakeRate === null) {
    return <span className="text-ink-3">—</span>;
  }

  const tone =
    user.wakeRate >= 70 ? 'good' : user.wakeRate >= 40 ? 'warn' : 'danger';

  return (
    <Badge tone={tone}>
      {formatPercent(user.wakeRate)}
      <span className="ml-1 font-normal opacity-70">
        {user.rewardEarnedCount}/
        {user.rewardEarnedCount + user.rewardMissedCount}
      </span>
    </Badge>
  );
}

// ── Detail ──────────────────────────────────────────────────────────────────

type DetailTab = 'overview' | 'alarms' | 'wallet' | 'payouts' | 'trial';

function UserDrawer({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const trail = useTrail();
  const onward = useOnwardTrail('user', userId);
  const [tab, setTab] = useState<DetailTab>('overview');

  const user = useQuery({
    queryKey: ['user', userId],
    queryFn: () => api.get<AdminUserDetail>(`/api/users/${userId}`),
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={user.data ? user.data.deviceId : t('common.loadingEllipsis')}
    >
      {user.isPending && <Spinner />}
      {user.isError && <ErrorNote error={user.error} />}

      {user.data && (
        <div className="space-y-5">
          <RecordNav trail={trail}>
            <Button
              icon={<AlarmClock size={15} />}
              onClick={() =>
                void navigate(deviceAlarmsPath(user.data.deviceId, onward))
              }
            >
              {t('users.viewAlarms', { count: user.data.alarmCount })}
            </Button>
          </RecordNav>

          <div>
            <Tabs
              label={t('users.tabsLabel')}
              value={tab}
              onChange={setTab}
              tabs={[
                { id: 'overview', label: t('users.tab.overview') },
                { id: 'alarms', label: t('users.tab.alarms') },
                { id: 'wallet', label: t('users.tab.wallet') },
                { id: 'payouts', label: t('users.tab.payouts') },
                { id: 'trial', label: t('users.tab.freeTrial') },
              ]}
            />

            {tab === 'overview' && (
              <Overview user={user.data} onClose={onClose} />
            )}
            {tab === 'alarms' && (
              <UserAlarmsTab
                userId={userId}
                deviceId={user.data.deviceId}
                trail={onward}
              />
            )}
            {tab === 'wallet' && <WalletTab user={user.data} />}
            {tab === 'payouts' && <PayoutsTab userId={userId} />}
            {tab === 'trial' && <FreeTrialTab userId={userId} />}
          </div>
        </div>
      )}
    </Drawer>
  );
}

function Overview({
  user,
  onClose,
}: {
  user: AdminUserDetail;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<UserDialog | null>(null);
  const [trialDays, setTrialDays] = useState('3');
  const [days, setDays] = useState('30');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['user', user.id] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const action = useMutation({
    mutationFn: async ({
      kind,
      reason,
    }: {
      kind: UserDialog;
      reason: string;
    }) => {
      switch (kind) {
        case 'activate':
        case 'deactivate':
          return api.patch(`/api/users/${user.id}`, {
            isActive: kind === 'activate',
            reason,
          });
        case 'delete':
          return api.delete(`/api/users/${user.id}`, { reason });
        case 'restore':
          return api.post(`/api/users/${user.id}/restore`, { reason });
        case 'subscribe':
          return api.post(`/api/users/${user.id}/subscription`, {
            days: Number(days) || 30,
            reason,
          });
        case 'unsubscribe':
          return api.delete(`/api/users/${user.id}/subscription`, { reason });
        case 'grantTrial':
          return api.post(`/api/users/${user.id}/free-trial`, {
            days: Number(trialDays) || 3,
            reason,
          });
        case 'revokeTrial':
          return api.delete(`/api/users/${user.id}/free-trial`, {
            reason,
          });
      }
    },
    onSuccess: (_data, variables) => {
      toast.success(t('common.done'));
      setDialog(null);
      refresh();
      if (variables.kind === 'delete') onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="space-y-5">
      {user.isDeleted && (
        <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
          {t('users.deleted', { when: formatRelative(user.deletedAt) })}
        </div>
      )}

      {user.silenced && (
        <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
          <span className="flex items-center gap-1.5 font-medium">
            <BellOff size={14} />{' '}
            {t('users.silencedBy', { actor: user.silenced.actor })}
          </span>
          {user.silenced.reason && (
            <p className="mt-0.5 text-xs">{user.silenced.reason}</p>
          )}
        </div>
      )}

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.section.wallet')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('users.row.balance')}>
            {formatCredits(user.balance)}
          </Row>
          <Row label={t('users.row.staked')}>
            {formatCredits(user.blockedBalance)}
          </Row>
          <Row label={t('users.row.spendable')}>
            {formatCredits(user.availableBalance)}
          </Row>
          <Row label={t('users.row.wonFromSettlements')}>
            {t('users.wonOver', {
              amount: formatCredits(user.earnedBalance),
              count: user.payoutCount,
            })}
          </Row>
        </dl>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.section.behaviour')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('users.row.alarms')}>
            {t('users.alarmsLive', {
              enabled: user.enabledAlarmCount,
              total: user.alarmCount,
            })}
          </Row>
          <Row label={t('users.row.challengesWon')}>
            {formatNumber(user.rewardEarnedCount)}
          </Row>
          <Row label={t('users.row.challengesMissed')}>
            {formatNumber(user.rewardMissedCount)}
          </Row>
          <Row label={t('users.row.wakeRate')}>
            {user.wakeRate === null
              ? t('users.nothingSettled')
              : formatPercent(user.wakeRate)}
          </Row>
        </dl>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.section.device')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('users.row.userId')} mono>
            <IdChip id={user.id} />
          </Row>
          <Row label={t('users.row.owner')}>
            {user.identity ? (
              <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5">
                <span>
                  {[user.identity.firstName, user.identity.lastName]
                    .filter(Boolean)
                    .join(' ') || t('users.ownerNameMissing')}
                </span>
                <span className="break-all text-ink-3">
                  {user.identity.email} ·{' '}
                  {user.identity.provider === 'apple' ? 'Apple' : 'Google'}
                </span>
              </span>
            ) : (
              t('users.ownerNone')
            )}
          </Row>
          {user.deviceMovedAt && (
            <Row label={t('users.row.deviceMoved')}>
              {formatDateTime(user.deviceMovedAt)}
            </Row>
          )}
          <Row label={t('users.row.platform')}>
            {formatPlatform(user.platform)}
            {user.phoneModel && ` · ${user.phoneModel}`}
          </Row>
          <Row label={t('users.row.appVersion')}>
            {user.currentAppVersion ?? '—'}
          </Row>
          <Row label={t('users.row.languageCountry')}>
            {user.language.toUpperCase()}
            {user.country && ` · ${user.country}`}
          </Row>
          <Row label={t('users.row.attestation')}>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={13} className="text-ink-3" />
              {humanize(user.integrityStatus)}
              {user.integrityReason && (
                <span className="text-ink-3">({user.integrityReason})</span>
              )}
            </span>
          </Row>
          <Row label={t('users.row.pushToken')}>
            {user.hasFcmToken ? t('users.pushPresent') : t('users.pushNone')}
          </Row>
          <Row label={t('users.row.lastIp')} mono>
            {user.ipAddress ?? '—'}
          </Row>
          <Row label={t('users.row.registered')}>
            {formatDateTime(user.registeredAt)}
          </Row>
          <Row label={t('users.row.lastSeen')}>
            {formatRelative(user.lastSeenAt)}
          </Row>
        </dl>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.section.subscription')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('users.row.status')}>
            {user.hasActiveSubscription ? (
              <Badge tone="good">{t('users.subActive')}</Badge>
            ) : (
              <Badge>{t('common.none')}</Badge>
            )}
          </Row>
          {user.subscribeEndAt && (
            <Row label={t('users.row.expires')}>
              {formatDateTime(user.subscribeEndAt)}
            </Row>
          )}
        </dl>

        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            icon={<CreditCard size={15} />}
            onClick={() => setDialog('subscribe')}
          >
            {t('users.grantSubscription')}
          </Button>
          {user.hasActiveSubscription && (
            <Button onClick={() => setDialog('unsubscribe')}>
              {t('users.cancelSubscription')}
            </Button>
          )}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.section.freeTrial')}
        </h3>
        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <Row label={t('users.row.status')}>
            {user.hasActiveFreeTrial ? (
              <Badge tone="good">{t('users.trialActive')}</Badge>
            ) : user.trialEndAt ? (
              <Badge>{t('users.trialEnded')}</Badge>
            ) : (
              <Badge>{t('common.none')}</Badge>
            )}
          </Row>
          {user.trialStartAt && (
            <Row label={t('users.row.trialStarted')}>
              {formatDateTime(user.trialStartAt)}
            </Row>
          )}
          {user.trialEndAt && (
            <Row label={t('users.row.trialEnds')}>
              {formatDateTime(user.trialEndAt)}
            </Row>
          )}
          {/* What is actually paying for the alarms, which is the answer a
              support question is usually after — the two sections above are
              how it got there. */}
          <Row label={t('users.row.accessMode')}>
            {t(`users.accessMode.${user.accessMode}`)}
          </Row>
        </dl>

        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            icon={<Gift size={15} />}
            onClick={() => setDialog('grantTrial')}
          >
            {user.hasActiveFreeTrial
              ? t('users.extendFreeTrial')
              : t('users.grantFreeTrial')}
          </Button>
          {user.hasActiveFreeTrial && (
            <Button onClick={() => setDialog('revokeTrial')}>
              {t('users.revokeFreeTrial')}
            </Button>
          )}
        </div>
      </section>

      <section className="border-t border-line pt-4">
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.section.account')}
        </h3>
        <div className="flex flex-wrap gap-2">
          {user.isActive ? (
            <Button
              icon={<UserX size={15} />}
              onClick={() => setDialog('deactivate')}
            >
              {t('users.deactivate')}
            </Button>
          ) : (
            <Button onClick={() => setDialog('activate')}>
              {t('users.reactivate')}
            </Button>
          )}
          {user.isDeleted ? (
            <Button
              icon={<RotateCcw size={15} />}
              onClick={() => setDialog('restore')}
            >
              {t('common.restore')}
            </Button>
          ) : (
            <Button
              variant="danger"
              icon={<Trash2 size={15} />}
              onClick={() => setDialog('delete')}
            >
              {t('common.delete')}
            </Button>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={dialog !== null}
        title={t(`users.dialog.${dialog ?? 'delete'}.title`)}
        description={t(`users.dialog.${dialog ?? 'delete'}.description`)}
        destructive={dialog === 'delete' || dialog === 'deactivate'}
        confirmLabel={
          dialog === 'delete' ? t('users.deleteAccount') : t('common.confirm')
        }
        loading={action.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          dialog && action.mutate({ kind: dialog, reason })
        }
      >
        {dialog === 'subscribe' && (
          <Field label={t('users.days')} hint={t('users.daysHint')}>
            <Input
              type="number"
              min={1}
              value={days}
              onChange={(event) => setDays(event.target.value)}
            />
          </Field>
        )}
        {dialog === 'grantTrial' && (
          <Field label={t('users.days')} hint={t('users.daysHint')}>
            <Input
              type="number"
              min={1}
              max={365}
              value={trialDays}
              onChange={(event) => setTrialDays(event.target.value)}
            />
          </Field>
        )}
      </ConfirmDialog>
    </div>
  );
}

/** The account actions that need a confirmation and a written reason. */
type UserDialog =
  | 'activate'
  | 'deactivate'
  | 'delete'
  | 'restore'
  | 'subscribe'
  | 'unsubscribe'
  | 'grantTrial'
  | 'revokeTrial';

// ── Alarms tab ──────────────────────────────────────────────────────────────

/**
 * What this account has actually set.
 *
 * The overview counts the alarms; this is the list behind that number, and the
 * way into every one of them. Reading a user's behaviour means reading their
 * alarms, and pasting an id into a separate page to do it was the long way
 * round.
 */
function UserAlarmsTab({
  userId,
  deviceId,
  trail,
}: {
  userId: string;
  deviceId: string;
  trail: Trail;
}) {
  const t = useT();
  const open = useOpenRecord();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const alarms = useQuery({
    queryKey: ['user-alarms', userId, page],
    queryFn: () =>
      api.get<Paginated<AdminAlarm>>('/api/alarms', {
        userId,
        page,
        limit: 25,
      }),
  });

  if (alarms.isPending) return <Skeleton className="h-64" />;
  if (alarms.isError) return <ErrorNote error={alarms.error} />;

  const items = alarms.data?.items ?? [];

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-3">
          {t('users.noAlarms')}
        </p>
      ) : (
        <div className="rounded-lg border border-line">
          <ul className="divide-y divide-line">
            {items.map((alarm) => (
              <li key={alarm.id}>
                <button
                  type="button"
                  onClick={() => open({ kind: 'alarm', id: alarm.id }, trail)}
                  className="w-full px-3 py-2.5 text-left transition-colors hover:bg-raised"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="tnum text-sm font-semibold text-ink">
                      {alarm.time}
                    </span>
                    <span className="min-w-0 truncate text-sm text-ink-2">
                      {alarm.label || t('alarms.noLabel')}
                    </span>
                    {!alarm.enabled && <Badge>{t('common.off')}</Badge>}
                    {alarm.heldAmount > 0 && (
                      <Badge tone="brand">
                        {formatNumber(alarm.heldAmount)}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-ink-3">
                    {t('users.alarmLine', {
                      schedule: formatSchedule(alarm.recurrence.label),
                      won: alarm.rewardEarnedCount,
                      missed: alarm.rewardMissedCount,
                    })}
                  </p>
                </button>
              </li>
            ))}
          </ul>
          <Pagination
            page={alarms.data?.page ?? 1}
            totalPages={alarms.data?.totalPages ?? 1}
            total={alarms.data?.total ?? 0}
            onChange={setPage}
          />
        </div>
      )}

      <Button
        className="w-full"
        icon={<AlarmClock size={15} />}
        onClick={() => void navigate(deviceAlarmsPath(deviceId, trail))}
      >
        {t('users.openInAlarmList')}
      </Button>
    </div>
  );
}

// ── Wallet tab ──────────────────────────────────────────────────────────────

const operations = (
  t: Translate,
): { value: WalletOperation; label: string; hint: string }[] => [
  {
    value: 'credit',
    label: t('users.op.credit'),
    hint: t('users.op.creditHint'),
  },
  { value: 'debit', label: t('users.op.debit'), hint: t('users.op.debitHint') },
  {
    value: 'release',
    label: t('users.op.release'),
    hint: t('users.op.releaseHint'),
  },
  { value: 'block', label: t('users.op.block'), hint: t('users.op.blockHint') },
  {
    value: 'forfeit',
    label: t('users.op.forfeit'),
    hint: t('users.op.forfeitHint'),
  },
];

function WalletTab({ user }: { user: AdminUserDetail }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [operation, setOperation] = useState<WalletOperation>('credit');
  const [amount, setAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [page, setPage] = useState(1);

  const history = useQuery({
    queryKey: ['user-wallet', user.id, page],
    queryFn: () =>
      api.get<Paginated<BalanceEvent>>(`/api/users/${user.id}/wallet`, {
        page,
        limit: 25,
      }),
  });

  const adjust = useMutation({
    mutationFn: (reason: string) =>
      api.post<WalletAdjustmentResult>(`/api/users/${user.id}/wallet`, {
        operation,
        amount: Number(amount),
        reason,
      }),
    onSuccess: (result) => {
      if (result.moved === 0) {
        toast.error(t('users.nothingMoved'));
      } else {
        toast.success(
          t('users.moved', { amount: formatCredits(result.moved) }),
        );
      }
      setConfirming(false);
      setAmount('');
      void queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      void queryClient.invalidateQueries({
        queryKey: ['user-wallet', user.id],
      });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const options = operations(t);
  const selected = options.find((option) => option.value === operation);

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-line p-3">
        <h3 className="mb-3 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.moveByHand')}
        </h3>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('users.operation')} hint={selected?.hint}>
            <Select
              value={operation}
              onChange={(event) =>
                setOperation(event.target.value as WalletOperation)
              }
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('users.amount')} hint={t('users.amountHint')}>
            <Input
              type="number"
              min={1}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0"
            />
          </Field>
        </div>

        <Button
          variant="primary"
          className="mt-3 w-full"
          icon={<Coins size={15} />}
          disabled={!amount || Number(amount) < 1}
          onClick={() => setConfirming(true)}
        >
          {selected?.label}
        </Button>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('users.ledger')}
        </h3>
        {history.isPending ? (
          <Skeleton className="h-64" />
        ) : (
          <div className="rounded-lg border border-line">
            <ul className="divide-y divide-line">
              {(history.data?.items ?? []).map((event) => (
                <li key={event.id} className="px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-ink">
                      {humanize(event.type)}
                    </span>
                    <span
                      className={
                        event.direction === 'credit'
                          ? 'tnum text-sm font-semibold text-good'
                          : event.direction === 'debit'
                            ? 'tnum text-sm font-semibold text-danger'
                            : 'tnum text-sm font-semibold text-ink-2'
                      }
                    >
                      {event.direction === 'credit'
                        ? '+'
                        : event.direction === 'debit'
                          ? '−'
                          : '·'}
                      {formatNumber(event.amount)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-ink-3">
                    {t('users.ledgerLine', {
                      reason: humanize(event.reason),
                      when: formatDateTime(event.occurredAt),
                      balance: formatNumber(event.balanceAfter),
                    })}
                  </p>
                </li>
              ))}
            </ul>
            <Pagination
              page={history.data?.page ?? 1}
              totalPages={history.data?.totalPages ?? 1}
              total={history.data?.total ?? 0}
              onChange={setPage}
            />
          </div>
        )}
      </section>

      <ConfirmDialog
        open={confirming}
        title={`${selected?.label ?? t('users.adjust')} — ${formatCredits(
          Number(amount) || 0,
        )}`}
        description={selected?.hint}
        destructive={operation === 'debit' || operation === 'forfeit'}
        confirmLabel={t('users.moveTheCredits')}
        loading={adjust.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={(reason) => adjust.mutate(reason)}
      />
    </div>
  );
}

// ── Payouts tab ─────────────────────────────────────────────────────────────

/**
 * Every grant, refusal and ending of this account's free trial.
 *
 * The refusals are why this exists. A trial that was refused leaves no trace
 * on the account at all — the columns look exactly like an account that was
 * never offered one — so the log is the only place "why has this user no
 * trial?" can be answered, and the only place "is this device being farmed?"
 * shows its working. Each row therefore carries the device and address it
 * happened on, not just the outcome.
 */
function FreeTrialTab({ userId }: { userId: string }) {
  const t = useT();
  const [page, setPage] = useState(1);

  const history = useQuery({
    queryKey: ['user-free-trial', userId, page],
    queryFn: () =>
      api.get<AdminFreeTrialEvents>(`/api/users/${userId}/free-trial`, {
        page,
        limit: 25,
      }),
  });

  if (history.isPending) return <Skeleton className="h-64" />;
  if (history.isError) return <ErrorNote error={history.error} />;

  const events = history.data?.events ?? [];
  if (events.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-3">
        {t('users.noTrialEvents')}
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-line">
      <ul className="divide-y divide-line">
        {events.map((event) => (
          <li key={event.id} className="px-3 py-2.5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Badge tone={trialTone(event.type)}>
                {t(`users.trialType.${event.type}`)}
              </Badge>
              <span className="text-xs text-ink-3">
                {formatDateTime(event.occurredAt)}
              </span>
            </div>
            {event.reason && (
              <p className="mt-1 text-sm text-ink">
                {t(`users.trialReason.${event.reason}`)}
              </p>
            )}
            {/* Wrapping and breakable: a device id is long, and an operator
                on a tablet should not have to scroll sideways to read one. */}
            <p className="mt-0.5 break-all text-xs text-ink-3">
              {[
                event.deviceId,
                event.ipAddress,
                event.actor && t('users.trialBy', { actor: event.actor }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {event.trialEndAt && (
              <p className="mt-0.5 text-xs text-ink-3">
                {t('users.trialWindow', {
                  from: event.trialStartAt
                    ? formatDateTime(event.trialStartAt)
                    : '—',
                  to: formatDateTime(event.trialEndAt),
                })}
              </p>
            )}
          </li>
        ))}
      </ul>
      <Pagination
        page={history.data?.page ?? 1}
        totalPages={history.data?.totalPages ?? 1}
        total={history.data?.total ?? 0}
        onChange={setPage}
      />
    </div>
  );
}

/** Green for a trial that started, red for one refused, neutral for the rest. */
function trialTone(type: string): 'good' | 'danger' | 'neutral' {
  if (type === 'granted' || type === 'extended') return 'good';
  if (type === 'denied' || type === 'revoked') return 'danger';
  return 'neutral';
}

function PayoutsTab({ userId }: { userId: string }) {
  const t = useT();
  const [page, setPage] = useState(1);

  const payouts = useQuery({
    queryKey: ['user-payouts', userId, page],
    queryFn: () =>
      api.get<Paginated<PayoutAllocation>>(`/api/users/${userId}/payouts`, {
        page,
        limit: 25,
      }),
  });

  if (payouts.isPending) return <Skeleton className="h-64" />;
  if (payouts.isError) return <ErrorNote error={payouts.error} />;

  const items = payouts.data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-3">
        {t('users.noPayouts')}
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-line">
      <ul className="divide-y divide-line">
        {items.map((allocation) => (
          <li key={allocation.id} className="px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-ink">{allocation.schemeName}</span>
              <span className="tnum text-sm font-semibold text-good">
                +{formatNumber(allocation.amount)}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-ink-3">
              {t('users.payoutLine', {
                rank: allocation.rank,
                weight: allocation.weight,
                percent: allocation.sharePercent,
                when: allocation.paidAt
                  ? formatDateTime(allocation.paidAt)
                  : humanize(allocation.status),
              })}
            </p>
          </li>
        ))}
      </ul>
      <Pagination
        page={payouts.data?.page ?? 1}
        totalPages={payouts.data?.totalPages ?? 1}
        total={payouts.data?.total ?? 0}
        onChange={setPage}
      />
    </div>
  );
}
